/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useMemo } from 'react';
import { styled } from '@superset-ui/core';
import {
  ControlComponentProps,
  safeParseEChartOptions,
  EChartOptionsParseError,
} from '@superset-ui/chart-controls';
import ControlHeader, {
  ControlHeaderProps,
} from 'src/explore/components/ControlHeader';
import { TextAreaEditor } from 'src/components/AsyncAceEditor';
import { EditorGlobalStyles } from 'src/components/AsyncAceEditor/editorStyles';
import { useDebounceValue } from 'src/hooks/useDebounceValue';

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale.light2};
  border-radius: ${({ theme }) => theme.gridUnit}px;
  overflow: hidden;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error.base};
  font-size: ${({ theme }) => theme.typography.sizes.s}px;
  padding: ${({ theme }) => theme.gridUnit}px;
  background: ${({ theme }) => theme.colors.error.light2};
  border-top: 1px solid ${({ theme }) => theme.colors.error.light1};
`;

const ErrorItem = styled.div`
  margin-bottom: ${({ theme }) => theme.gridUnit / 2}px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export interface JSEditorControlProps
  extends Omit<ControlHeaderProps, 'name' | 'validationErrors'>,
    ControlComponentProps {}

export default function JSEditorControl({
  name,
  label,
  description,
  renderTrigger,
  hovered,
  tooltipOnClick,
  onChange,
  value,
}: JSEditorControlProps) {
  const debouncedValue = useDebounceValue(value);

  const error = useMemo(() => {
    try {
      safeParseEChartOptions(
        typeof debouncedValue === 'string' ? debouncedValue : undefined,
      );
      return null;
    } catch (err) {
      if (err instanceof EChartOptionsParseError) {
        return err;
      }
      throw err;
    }
  }, [debouncedValue]);

  const headerProps = {
    name,
    label: label ?? name,
    description,
    renderTrigger,
    validationErrors: error?.message ? [error.message] : undefined,
    hovered,
    tooltipOnClick,
  };

  return (
    <>
      <EditorGlobalStyles />
      <ControlHeader {...headerProps} />
      <Container data-ace-frame="">
        <TextAreaEditor
          name={name}
          value={value ?? '{}'}
          onChange={(val: string) => onChange?.(val)}
          mode="javascript"
          theme="github"
          tabSize={2}
          lineNumbers
          width="100%"
          height="250px"
          editorProps={{ $blockScrolling: true }}
        />
      </Container>
      {error && (
        <ErrorMessage>
          {error.validationErrors.length > 0 ? (
            error.validationErrors.map((err, idx) => (
              <ErrorItem key={idx}>{err}</ErrorItem>
            ))
          ) : (
            <ErrorItem>{error.message}</ErrorItem>
          )}
        </ErrorMessage>
      )}
    </>
  );
}
